/* PLAT-03: language and region. The settings screen's own strings live here in en and es; the safety
   module's catalogs (`GET /api/v1/i18n/:lang`) are merged in when the API is reachable, so keys this file
   lacks (report.*, security.*) come from the server. Choosing a UI language also sets <html lang dir>. */
import { useEffect, useMemo, useState } from 'react';
import { api } from '../../lib/api';

export type Catalog = Record<string, string>;

export const EN: Catalog = {
  'settings.title': 'Settings & safety',
  'settings.list': 'Settings',
  'section.account': 'Account',
  'section.privacy': 'Privacy',
  'section.privacyCenter': 'Privacy center',
  'section.safety': 'Safety & security',
  'section.personal': 'Personalization',
  'section.offline': 'Offline & sync',
  'section.devices': 'Devices',
  'hint.privacy': 'Training off · export · delete',
  'hint.safety': 'Age, labels, reports, sign-in',
  'hint.personal': 'Language, accessibility, notifications',
  'hint.devices': 'Phone, PC, web, extension',
  'common.more': 'More',
  'common.save': 'Save',
  'common.cancel': 'Cancel',
  'common.close': 'Close',
  'common.showAdvanced': 'Show advanced ({n})',
  'privacy.training': 'Use my content to train models',
  'privacy.trainingNote': 'Off for everyone and cannot be turned on by an admin.',
  'privacy.memory': 'Memory',
  'privacy.reference': 'Reference past chats',
  'privacy.sensitive': 'Sensitive topics in memory',
  'privacy.temporary': 'Temporary chats by default',
  'privacy.retention': 'Auto-delete chats after',
  'privacy.export': 'Export everything',
  'privacy.deleteAll': 'Delete everything',
  'privacy.deleteAccount': 'Delete account',
  'privacy.subprocessors': 'Who processes your data',
  'safety.age': 'Age & eligibility',
  'safety.disclosure': 'AI disclosure & labeling',
  'safety.moderation': 'Moderation',
  'safety.reports': 'Your reports',
  'safety.provenance': 'Content credentials',
  'safety.security': 'Account security',
  'safety.legal': 'Legal',
  'safety.defaults': 'Consumer defaults',
  'personal.language': 'Language & region',
  'personal.accessibility': 'Accessibility',
  'personal.notifications': 'Notifications',
  'lang.ui': 'App language',
  'lang.answers': 'Answer language',
  'lang.voice': 'Voice language',
  'lang.region': 'Region',
  'lang.units': 'Units',
  'lang.autoTranslate': 'Auto-translate',
  'a11y.reduceMotion': 'Reduce motion',
  'a11y.largerText': 'Larger text',
  'a11y.highContrast': 'High contrast',
  'a11y.captions': 'Captions always on',
  'a11y.screenReader': 'Screen reader optimized',
  'a11y.voiceControl': 'Voice control',
  'offline.title': 'Offline & sync',
  'offline.clear': 'Clear cache',
  'offline.syncNow': 'Sync now',
  'account.signOutAll': 'Sign out everywhere',
  'account.signOut': 'Sign out',
};

export const ES: Catalog = {
  'settings.title': 'Ajustes y seguridad',
  'settings.list': 'Ajustes',
  'section.account': 'Cuenta',
  'section.privacy': 'Privacidad',
  'section.privacyCenter': 'Centro de privacidad',
  'section.safety': 'Seguridad',
  'section.personal': 'Personalización',
  'section.offline': 'Sin conexión y sincronización',
  'section.devices': 'Dispositivos',
  'hint.privacy': 'Entrenamiento desactivado · exportar · eliminar',
  'hint.safety': 'Edad, etiquetas, denuncias, inicio de sesión',
  'hint.personal': 'Idioma, accesibilidad, notificaciones',
  'hint.devices': 'Teléfono, PC, web, extensión',
  'common.more': 'Más',
  'common.save': 'Guardar',
  'common.cancel': 'Cancelar',
  'common.close': 'Cerrar',
  'common.showAdvanced': 'Mostrar opciones avanzadas ({n})',
  'privacy.training': 'Usar mi contenido para entrenar modelos',
  'privacy.trainingNote': 'Desactivado para todos; un administrador no puede activarlo.',
  'privacy.memory': 'Memoria',
  'privacy.reference': 'Consultar chats anteriores',
  'privacy.sensitive': 'Temas sensibles en la memoria',
  'privacy.temporary': 'Chats temporales por defecto',
  'privacy.retention': 'Eliminar chats automáticamente tras',
  'privacy.export': 'Exportar todo',
  'privacy.deleteAll': 'Eliminar todo',
  'privacy.deleteAccount': 'Eliminar cuenta',
  'privacy.subprocessors': 'Quién procesa tus datos',
  'safety.age': 'Edad y requisitos',
  'safety.disclosure': 'Aviso y etiquetado de IA',
  'safety.moderation': 'Moderación',
  'safety.reports': 'Tus denuncias',
  'safety.provenance': 'Credenciales de contenido',
  'safety.security': 'Seguridad de la cuenta',
  'safety.legal': 'Aspectos legales',
  'safety.defaults': 'Protección del consumidor',
  'personal.language': 'Idioma y región',
  'personal.accessibility': 'Accesibilidad',
  'personal.notifications': 'Notificaciones',
  'lang.ui': 'Idioma de la app',
  'lang.answers': 'Idioma de las respuestas',
  'lang.voice': 'Idioma de voz',
  'lang.region': 'Región',
  'lang.units': 'Unidades',
  'lang.autoTranslate': 'Traducción automática',
  'a11y.reduceMotion': 'Reducir movimiento',
  'a11y.largerText': 'Texto más grande',
  'a11y.highContrast': 'Alto contraste',
  'a11y.captions': 'Subtítulos siempre activados',
  'a11y.screenReader': 'Optimizado para lector de pantalla',
  'a11y.voiceControl': 'Control por voz',
  'offline.title': 'Sin conexión y sincronización',
  'offline.clear': 'Borrar caché',
  'offline.syncNow': 'Sincronizar ahora',
  'account.signOutAll': 'Cerrar sesión en todas partes',
  'account.signOut': 'Cerrar sesión',
};

export const LOCAL_CATALOGS: Record<string, Catalog> = { en: EN, es: ES };
export const RTL = new Set(['ar', 'he', 'fa', 'ur']);

/** `es-MX` -> `es`; unknown -> `en`. */
export function baseLanguage(tag: string | undefined): string {
  const b = (tag ?? 'en').toLowerCase().split('-')[0] || 'en';
  return b;
}

export function translate(catalog: Catalog, key: string, vars?: Record<string, string | number>): string {
  const raw = catalog[key] ?? EN[key] ?? key;
  return vars ? raw.replace(/\{(\w+)\}/g, (_, k: string) => String(vars[k] ?? `{${k}}`)) : raw;
}

export function applyLanguage(tag: string, root: HTMLElement = document.documentElement): void {
  const lang = baseLanguage(tag);
  root.lang = tag && tag !== 'auto' ? tag : 'en';
  root.dir = RTL.has(lang) ? 'rtl' : 'ltr';
}

export const LANG_KEY = 'cx.settings.lang';

/** Local catalog first, then the server's catalog merged on top (cached per language for the session). */
const serverCache = new Map<string, Catalog>();
export function useCatalog(tag: string): (key: string, vars?: Record<string, string | number>) => string {
  const lang = baseLanguage(tag);
  const [server, setServer] = useState<Catalog | null>(serverCache.get(lang) ?? null);
  useEffect(() => {
    let live = true;
    if (serverCache.has(lang)) { setServer(serverCache.get(lang)!); return; }
    setServer(null);
    api.get<{ messages?: Catalog }>(`/api/v1/i18n/${encodeURIComponent(lang)}`)
      .then((r) => { if (r?.messages) { serverCache.set(lang, r.messages); if (live) setServer(r.messages); } })
      .catch(() => undefined);
    return () => { live = false; };
  }, [lang]);
  return useMemo(() => {
    // Server keys fill in what the local catalog lacks (report.*, security.*, ...); local keys win.
    const cat: Catalog = { ...(server ?? {}), ...(LOCAL_CATALOGS[lang] ?? EN) };
    return (key: string, vars?: Record<string, string | number>) => translate(cat, key, vars);
  }, [lang, server]);
}
